// ViewerHeader.jsx
import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, SvgIcon, Typography } from "@mui/material";
import logo from "../assets/logo.png";
import AttributionBox from "./AttributionBox";
import { viewerSubtleIconButtonSx } from "./unityWebViewer/panelStyles";

const InformationIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
    <path
      d="M12 10.5V17M12 7.2H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.1"
    />
  </SvgIcon>
);

const ViewerHeader = ({ system }) => {
  const headerRef = useRef(null);
  const [informationOpen, setInformationOpen] = useState(false);
  const informationPanelId = "viewer-experience-information-panel";

  useEffect(() => {
    const headerElement = headerRef.current;
    if (!headerElement) return undefined;

    const updateHeaderHeight = () => {
      if (!headerElement.isConnected) return;

      const height = headerElement.getBoundingClientRect().height;
      document.documentElement.style.setProperty(
        "--app-header-height",
        `${Math.ceil(height)}px`
      );
    };

    updateHeaderHeight();

    const observer = new ResizeObserver(updateHeaderHeight);
    observer.observe(headerElement);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--app-header-height");
    };
  }, []);

  useEffect(() => {
    if (!informationOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setInformationOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setInformationOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [informationOpen]);

  const toggleInformationPanel = (event) => {
    event.stopPropagation();
    setInformationOpen((open) => !open);
  };

  return (
    <Box>
      <Box
        className="viewer-header"
        ref={headerRef}
        sx={{
          position: "absolute",
          top: 0,
          left: "env(safe-area-inset-left, 0px)",
          right: "env(safe-area-inset-right, 0px)",
          width: "auto",
          color: "#fff",
          px: "var(--viewer-header-px, 16px)",
          py: "var(--viewer-header-py, 10px)",
          minHeight: "var(--viewer-header-height, 80px)",
          zIndex: "var(--start-overlay-chrome-z-index, 2010)",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          alignItems: "center",
          pointerEvents: "none",
        }}
      >
        <Box
          className="viewer-header__brand"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "clamp(8px, 1.2vw, 14px)",
            position: "relative",
            px: "clamp(10px, 1.4vw, 16px)",
            py: "clamp(7px, 1vw, 11px)",
            borderRadius: "var(--radius-lg, 14px)",
            backgroundColor: "var(--surface-bg, rgba(9, 12, 18, 0.62))",
            boxShadow: "var(--surface-shadow, 0 14px 34px rgba(0, 0, 0, 0.34))",
            backdropFilter:
              "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
            WebkitBackdropFilter:
              "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
            minWidth: 0,
            maxWidth: "min(92vw, 860px)",
            pointerEvents: "auto",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img src={logo} alt="Logo" className="viewer-logo" />
          </Box>
          {system?.name && (
            <Box sx={{ minWidth: 0, textAlign: "left", flex: "1 1 auto" }}>
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  opacity: 0.72,
                  letterSpacing: "0.12em",
                  lineHeight: 1.1,
                  fontSize: "0.62rem",
                }}
              >
                Experience
              </Typography>
              <Typography
                component="h2"
                sx={{
                  m: 0,
                  lineHeight: 1.15,
                  fontSize: "clamp(0.88rem, 1.35vw, 1.12rem)",
                  fontWeight: 650,
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "min(58vw, 720px)",
                }}
              >
                {system.name}
              </Typography>
            </Box>
          )}
          <IconButton
            aria-label="Show experience information"
            aria-expanded={informationOpen}
            aria-controls={informationOpen ? informationPanelId : undefined}
            onClick={toggleInformationPanel}
            size="small"
            sx={{
              ...viewerSubtleIconButtonSx,
              flexShrink: 0,
              color: informationOpen
                ? "rgba(242, 255, 252, 0.92)"
                : viewerSubtleIconButtonSx.color,
              background: informationOpen
                ? "rgba(210, 255, 249, 0.11)"
                : viewerSubtleIconButtonSx.background,
              borderColor: informationOpen
                ? "rgba(210, 255, 249, 0.22)"
                : "rgba(210, 255, 249, 0.1)",
              opacity: informationOpen ? 1 : viewerSubtleIconButtonSx.opacity,
              "& .MuiSvgIcon-root": {
                fontSize: 17,
              },
            }}
          >
            <InformationIcon />
          </IconButton>
          <AttributionBox id={informationPanelId} open={informationOpen} />
        </Box>
      </Box>
    </Box>
  );
};

export default ViewerHeader;
